import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Zap,
  Target,
  Heart,
  Users,
  Lightbulb,
  CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative py-24 overflow-hidden" data-testid="about-hero">
      <div className="absolute inset-0 bg-[#0E2A47]" />
      <div className="absolute inset-0 grid-overlay" />
      <div className="absolute inset-0 spotlight" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.h1 
              variants={fadeInUp}
              className="font-heading text-4xl sm:text-5xl font-bold text-white leading-tight mb-6"
            >
              We Fix Broken Workflows.{' '}
              <span className="text-[#0FA4A9]">That's It.</span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="font-body text-lg text-white/70 mb-8"
            >
              Discovify AI LLC is an AI & Automation consultancy focused on real-world workflows, not theory. We help businesses find where they're leaking time and money, then fix it with AI and automation.
            </motion.p>

            <motion.div variants={fadeInUp}>
              <Link to="/contact">
                <Button 
                  data-testid="about-cta-btn"
                  className="bg-[#0FA4A9] hover:bg-[#0FA4A9]/90 text-white font-semibold uppercase tracking-wide px-8 py-6 rounded-sm text-base btn-shine transition-all duration-300 hover:shadow-[0_0_30px_-5px_rgba(15,164,169,0.6)]"
                >
                  Let's Talk
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>

          {/* Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:block"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_fix-workflows/artifacts/38gs4wbc_IMG_5031.jpeg"
              alt="B.J. presenting at an AI workshop"
              className="rounded-sm border border-white/10"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Founder Section
const FounderSection = () => {
  return (
    <section className="py-24 bg-[#0A1F35]" data-testid="founder-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid lg:grid-cols-2 gap-16 items-center"
        >
          {/* Image */}
          <motion.div variants={fadeInUp} className="order-2 lg:order-1">
            <div className="relative text-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_fix-workflows/artifacts/6on56xsq_Image%20Monacelli.png"
                alt="Founder"
                className="aspect-square max-w-md mx-auto rounded-sm border border-white/10 object-contain object-center bg-[#133456]"
              />
              <div className="mt-4">
                <p className="font-heading text-lg font-bold text-white">Discovify AI Founder</p>
                <p className="font-body text-[#0FA4A9]">B.J. Monacelli</p>
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div variants={fadeInUp} className="order-1 lg:order-2">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
              The Story
            </h2>
            <div className="space-y-4 font-body text-white/70">
              <p>
                My AI journey started at the 2024 NFF Campbell Trophy Summit when I heard Stanford's Jeremy Utley give a presentation about AI that completely changed my perspective. That moment sparked everything. I spent the next two years learning everything I could about AI and automation, building workflows, and discovering what actually works in real businesses.
              </p>
              <p>
                I started Discovify AI because I realized many businesses were curious about AI but just didn't know where to start. That's where I come in, helping you start your AI journey, just like that speech did for me. I'm a hands-on operator who builds automations for real businesses. I don't disappear after a strategy deck; I make sure it works for the people using it every day.
              </p>
              <p>
                I'm part of communities like <span className="text-[#0FA4A9]">Playmakers AI</span>, constantly learning and sharing what works. Because AI moves fast, and staying stagnant means falling behind.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// What We Believe Section
const BeliefsSection = () => {
  const beliefs = [
    {
      icon: Target,
      title: "ROI First",
      description: "Every automation we build should pay for itself in saved time or increased revenue."
    },
    {
      icon: Zap,
      title: "Practical Over Theoretical",
      description: "We don't just write strategy decks. We build things that actually provide tangible results for your business."
    },
    {
      icon: Heart,
      title: "Accessible to All",
      description: "AI shouldn't be reserved for enterprises. Local and growing businesses deserve it too."
    },
    {
      icon: Lightbulb,
      title: "Simple Wins",
      description: "The best automation is the one your team actually uses. Complexity kills adoption."
    }
  ];

  return (
    <section className="py-24 bg-[#0E2A47]" data-testid="beliefs-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4"
          >
            What We Believe
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-8"
        >
          {beliefs.map((belief, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="flex items-start gap-6"
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-sm bg-[#0FA4A9]/10 flex items-center justify-center">
                <belief.icon className="h-6 w-6 text-[#0FA4A9]" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-white mb-2">
                  {belief.title}
                </h3>
                <p className="font-body text-white/60">
                  {belief.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Community Section
const CommunitySection = () => {
  return (
    <section className="py-24 bg-[#0A1F35]" data-testid="community-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center"
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6"
          >
            Part of the Community
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="font-body text-lg text-white/60 max-w-2xl mx-auto mb-12"
          >
            We learn, share, and grow with the best in AI
          </motion.p>

          <motion.div 
            variants={fadeInUp}
            className="flex justify-center items-center gap-8 flex-wrap"
          >
            <div className="px-8 py-4 rounded-sm bg-white/5 border border-white/10">
              <span className="font-heading text-xl font-bold text-white/60">Playmakers AI</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-24 bg-[#0E2A47]" data-testid="about-cta-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6"
          >
            Ready to Work Together?
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="font-body text-lg text-white/60 mb-10"
          >
            Let's find out where your business is leaking time and fix it.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <Link to="/contact">
              <Button 
                data-testid="about-final-cta-btn"
                className="bg-[#E53935] hover:bg-[#E53935]/90 text-white font-bold uppercase tracking-widest px-10 py-6 rounded-sm text-lg btn-shine transition-all duration-300 hover:shadow-[0_0_30px_-5px_rgba(229,57,53,0.6)]"
              >
                Let's Talk
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <HeroSection />
      <FounderSection />
      <BeliefsSection />
      <CommunitySection />
      <CTASection />
    </div>
  );
}
